import React, { useState } from 'react'
import { CompletionQuestionsModalMetaProps, TrainingQuestion } from '../data/types'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'
import { API_BASE_URL } from '../data'

type Props = {
    meta: CompletionQuestionsModalMetaProps
    questions: TrainingQuestion[]
    isOpen: boolean
    onClose: () => void
    onComplete?: () => void  // Called when module is marked complete (updates parent state without API call)
    previewMode?: boolean
    trainingId?: string
    volunteerId?: number
    moduleIndex?: number
}

export const CompletionQuestionsModal = (props: Props) => {
    const { meta, questions, isOpen, onClose, onComplete, previewMode = false, trainingId, volunteerId, moduleIndex } = props;
    const [formData, setFormData] = useState({
        answers: [] as { questionIndex: number; answerIndex: number }[]
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showResults, setShowResults] = useState(false);

    // Handle option selection
    const handleAnswerSelect = (questionIndex: number, answerIndex: number) => {
        setFormData((prev) => {
            const newAnswers = [...prev.answers];
            newAnswers[questionIndex] = { questionIndex, answerIndex };
            return { ...prev, answers: newAnswers };
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // If results are already shown, close the modal
            if (showResults) {
                if (onComplete) {
                    onComplete()
                }
                resetFormData()
                onClose()
                return
            }

            // Check if all answers have been filled
            const allAnswered = questions.every((q, i) => formData.answers[i] !== undefined)
            if (!allAnswered) {
                setLoading(false)
                throw new Error("You must answer all questions before submitting.")
            }

            // Calculate results
            const answers = formData.answers.map(a => a.answerIndex)
            const results = formData.answers.map((a, i) => a.answerIndex === questions[i].answer)

            // Save quiz results and mark module complete
            if (!previewMode && trainingId && volunteerId !== undefined && moduleIndex !== undefined) {
                // Save quiz results (non-critical)
                try {
                    const response = await fetch(
                        `${API_BASE_URL}/training/${trainingId}/volunteer/${volunteerId}/module/${moduleIndex}/quiz`,
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ answers, results }),
                        }
                    )
                    if (response.ok) {
                        const result = await response.json()
                        console.log('Quiz results:', result.saved ? 'saved' : 'already exist')
                    }
                } catch (err) {
                    console.error('Error saving quiz results:', err)
                }

                // Mark module as complete (critical)
                const completeResponse = await fetch(
                    `${API_BASE_URL}/training/${trainingId}/volunteer/${volunteerId}/module/${moduleIndex}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ completed: true }),
                    }
                )
                if (!completeResponse.ok) {
                    throw new Error('Failed to mark module as complete. Please try again.')
                }
            }

            // Show results so user can see which answers were correct/incorrect
            setShowResults(true)

        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false)
        }
    };

    const resetFormData = () => {
        setFormData({
            answers: [] as { questionIndex: number; answerIndex: number }[]
        })
        setError(null)
        setShowResults(false)
    }

    const handleClose = () => {
        const wasShowingResults = showResults
        resetFormData()
        if (wasShowingResults && onComplete) {
            // Module was already marked complete via API, just update parent state
            onComplete()
        }
        onClose()
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={previewMode ? 'Preview Questions' : meta.title}
            maxWidth="2xl"
            footer={
                previewMode ? (
                    <div className="flex justify-end">
                        <Button onClick={handleClose} variant="outline" color="neutral">
                            Close
                        </Button>
                    </div>
                ) : (
                    <div className="flex justify-end gap-3">
                        {!showResults && (<Button onClick={handleClose} disabled={loading} variant="outline" color="neutral">
                            {meta.cancelButton}
                        </Button>)}
                        <Button
                            type="submit"
                            form="training-complete-form"
                            disabled={loading}
                            color='success'
                        >
                            {loading
                                ? meta.submittingButton
                                : showResults
                                    ? 'Close'
                                    : meta.submitButton
                            }
                        </Button>
                    </div>
                )
            }
        >
            <form id="training-complete-form" onSubmit={handleSubmit}>
                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                    {previewMode && (
                        <div className="mb-4 bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 px-4 py-3 rounded border border-primary-200 dark:border-primary-800">
                            <strong>Admin Preview Mode:</strong> This is a preview of the questions volunteers will see. Correct answers are shown below.
                        </div>
                    )}
                    {error && (
                        <div className="mb-4 bg-danger-100 text-danger-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    {questions.map((question, qIndex) => {
                        const userAnswer = formData.answers[qIndex]?.answerIndex
                        const isCorrect = userAnswer === question.answer
                        const hasAnswer = userAnswer !== undefined

                        return (
                            <div
                                key={qIndex}
                                className="mb-4 rounded-lg bg-white dark:bg-gray-800 shadow-sm"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                        Question {qIndex + 1}
                                    </h3>
                                    {(showResults || previewMode) && hasAnswer && (
                                        <span className={`text-xs font-semibold px-2 py-1 rounded ${isCorrect
                                            ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                                            : 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400'
                                        }`}>
                                            {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                                        </span>
                                    )}
                                </div>

                                <p className="text-gray-600 dark:text-gray-300 text-sm">{question.text}</p>

                                <div className="grid md:grid-cols-2 gap-2 mt-2">
                                    {question.options.map((option, oIndex) => {
                                        const isSelected = userAnswer === oIndex
                                        const isCorrectAnswer = question.answer === oIndex

                                        // Determine styling based on results state
                                        let optionClass = "flex items-center gap-2 p-2 rounded-lg border cursor-pointer"

                                        if (showResults || previewMode) {
                                            if (isCorrectAnswer) {
                                                optionClass += " border-success-500 bg-success-50 dark:bg-success-900/20"
                                            } else if (isSelected) {
                                                optionClass += " border-danger-500 bg-danger-50 dark:bg-danger-900/20"
                                            } else {
                                                optionClass += " border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40 opacity-60"
                                            }
                                        } else {
                                            optionClass += " border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40"
                                        }

                                        return (
                                            <label
                                                key={oIndex}
                                                className={optionClass}
                                            >
                                                <input
                                                    type="radio"
                                                    name={`question-${qIndex}`}
                                                    checked={isSelected}
                                                    onChange={() => handleAnswerSelect(qIndex, oIndex)}
                                                    disabled={showResults || previewMode}
                                                    className="w-4 h-4 text-primary-600"
                                                />

                                                <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                                                    {option}
                                                </span>

                                                {(showResults || previewMode) && isCorrectAnswer && (
                                                    <span className="text-xs text-success-600 dark:text-success-400 font-semibold">
                                                        ✓
                                                    </span>
                                                )}
                                            </label>
                                        )
                                    })}
                                </div>

                                {/* Show answer description and correct answer info */}
                                {(showResults || previewMode) && (
                                    <div className="mt-3 space-y-2">
                                        {!previewMode && !isCorrect && hasAnswer && (
                                            <div className="text-sm p-2 rounded bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800">
                                                <span className="font-semibold text-danger-700 dark:text-danger-400">
                                                    Correct answer:
                                                </span>
                                                <span className="text-danger-600 dark:text-danger-300 ms-1">
                                                    {question.options[question.answer]}
                                                </span>
                                            </div>
                                        )}
                                        {question.answer_description && (
                                            <div className="text-sm p-2 rounded bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
                                                <span className="font-semibold text-primary-700 dark:text-primary-400">
                                                    Explanation:
                                                </span>
                                                <p className="text-primary-600 dark:text-primary-300 mt-1">
                                                    {question.answer_description}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </form>
        </Modal>
    );
};

